import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Note } from 'app/models/dto/note.model';
import { MatExpansionPanel } from '@angular/material/expansion';
import { DealNote } from 'app/models/dto/dealNote.model';

@Component({
    selector: 'app-notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotesComponent implements OnInit, OnDestroy {

  @Input() borrowerId: number;
  @Input() borrowerName: string;
  @Input() dealId: number;
  @Input() dealNotes: DealNote[];
  @Input() draft: Note;
  @Input() expandStatus: boolean;
  @Input() hideHeader: boolean = false;
  @Input() inContainer: boolean = false;
  @Input() lenderUserId: number;
  @Input() notes: Note[];
  @Input() useDealNotes: boolean = false;

  @Output() addNote = new EventEmitter<Note>();
  @Output() deleteNote = new EventEmitter<number>();
  @Output() editNote = new EventEmitter<{ id: number, content: string }>();
  @Output() enterEditNote = new EventEmitter();
  @Output() saveDraft = new EventEmitter<string>();

  @ViewChild('addNotePanel', { static: true })
  addNotePanel: MatExpansionPanel;

  @ViewChild('contentTextArea', { static: true })
  contentTextArea: ElementRef;

  noteForm: UntypedFormGroup;
  editNoteForm: UntypedFormGroup;
  editNoteId: number | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.noteForm = this.fb.group({
      content: [this.draft.content || '', [Validators.required]]
    });
  }

  ngOnDestroy() {
    this.saveDraft.emit(this.noteForm.value);
  }

  get borrowerNotes() {
    if (this.useDealNotes && this.dealNotes) {
      return new Set(this.dealNotes.filter(note => {
        return note.dealId === this.dealId;
      }));
    } else {
      return new Set(this.notes.filter(note => {
        return note.borrowerId === this.borrowerId;
      }));
    }
  }

  openAddNote(): void {
    this.addNotePanel.open();
  }

  handleAfterExpand(): void {
    this.noteForm.markAsPristine();
    this.contentTextArea.nativeElement.focus();
  }

  handleAfterCollapse(): void {
    // reset the note form so we don't see validation errors on next open
    this.contentTextArea.nativeElement.blur();
    this.noteForm.markAsPristine();
  }

  handleAddNote(): void {
    this.addNote.emit(this.noteForm.value);
    this.expandStatus = false;
    this.noteForm.reset();
  }

  cancelEdit(): void {
    this.editNoteForm.reset();
    this.editNoteId = null;
  }

  handleDeleteNote(noteId): void {
    this.deleteNote.emit(noteId);
  }

  enterEditMode(note: Note): void {
    this.enterEditNote.emit();
    if (note.id) {
      this.editNoteId = note.id;
      this.expandStatus = false;
      this.editNoteForm = this.fb.group({
        content: [note.content]
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  handleEditNote(noteId: number): void {
    this.editNoteId = null;
    const content = this.editNoteForm.value.content;
    if (content === '') {
      this.handleDeleteNote(noteId);
    } else {
      if (this.contentWasUnchanged(noteId)) {
        return;
      }
      this.editNote.emit({
        id: noteId,
        content: content
      });
    }
  }

  getTimestamp(date: string) {
    return new Date(date).getTime();
  }

  get content(): AbstractControl {
    return this.noteForm.get('content') as AbstractControl;
  }

  private contentWasUnchanged(id: number) {
    if (this.useDealNotes) {
      return this.dealNotes.some(note => note.id === id && note.content === this.editNoteForm.value.content);
    } else {
      return this.dealNotes.some(note => note.id === id && note.content === this.editNoteForm.value.content);
    }
  }
}
